/**
 * UserPasswordResetRequestPage
 * [UC-041] Password reset request via email
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../core/api-client';
import { PasswordResetRequest } from '../types';

export function UserPasswordResetRequestPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setErrorMessage(t('userPasswordResetRequest.validation.emailRequired', 'Email is required'));
      return;
    }

    if (!email.includes('@')) {
      setErrorMessage(t('userPasswordResetRequest.validation.invalidEmail', 'Please enter a valid email address'));
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      await api.post<void>('/auth/forgot-password', { email } as PasswordResetRequest);
      setSuccessMessage(t('userPasswordResetRequest.success.emailSent', 'Reset instructions have been sent to your email address'));
      setEmail('');
    } catch (error: any) {
      console.error('Password reset request failed:', error);
      const status = error.response?.status;
      switch (status) {
        case 404:
          setErrorMessage(t('userPasswordResetRequest.errors.emailNotFound', 'Email not found - please check or register a new account'));
          break;
        case 409:
          setErrorMessage(t('userPasswordResetRequest.errors.accountDeactivated', 'Account is deactivated - please contact support'));
          break;
        case 502:
          setErrorMessage(t('userPasswordResetRequest.errors.emailServiceUnavailable', 'Email service unavailable - please try again later or contact support'));
          break;
        default:
          setErrorMessage(t('userPasswordResetRequest.errors.requestFailed', 'Failed to send reset email. Please try again.'));
          break;
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/user-login');
  };

  return (
    <div className="min-h-screen bg-[var(--surface-ground)] flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-sm sm:max-w-md">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-[var(--surface-raised)] mb-4">
            <svg className="w-6 h-6 text-[var(--accent-primary)]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
            </svg>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[var(--text-primary)]">
            {t('userPasswordResetRequest.title', 'Reset Password')}
          </h1>
          <p className="mt-2 text-sm text-[var(--text-muted)]">
            {t('userPasswordResetRequest.subtitle', "Enter your email and we'll send a reset link")}
          </p>
        </div>

        {/* Reset Form */}
        <div className="bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] p-4 sm:p-6">
          <form onSubmit={handleSubmit}>
            {/* Email Field */}
            <div>
              <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5">
                {t('userPasswordResetRequest.fields.email', 'Email address')}
              </label>
              <input
                type="email"
                placeholder={t('userPasswordResetRequest.placeholders.email', 'you@example.com')}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errorMessage) setErrorMessage('');
                  if (successMessage) setSuccessMessage('');
                }}
                disabled={isSubmitting}
                className="w-full px-3 py-2.5 sm:py-2 bg-[var(--surface-raised)] border border-[var(--border-subtle)] rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>

            {/* Error Message */}
            {errorMessage && (
              <div className="mt-4 bg-red-600/15 border border-red-600/25 text-red-700 px-4 py-3 rounded-lg text-sm">
                {errorMessage}
              </div>
            )}

            {/* Success Message */}
            {successMessage && (
              <div className="mt-4 bg-green-600/15 border border-green-600/25 text-green-700 px-4 py-3 rounded-lg text-sm">
                {successMessage}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting || !email}
              className="w-full mt-6 px-4 py-2.5 bg-[var(--accent-primary)] text-white rounded-lg hover:bg-[var(--accent-secondary)] disabled:bg-[var(--surface-overlay)] disabled:text-[var(--text-muted)] disabled:cursor-not-allowed transition-colors text-sm font-medium"
            >
              {isSubmitting ? t('userPasswordResetRequest.buttons.sending', 'Sending...') : t('userPasswordResetRequest.buttons.sendResetLink', 'Send Reset Link')}
            </button>
          </form>
        </div>

        {/* Back to Login Link */}
        <div className="mt-6 text-center">
          <button
            onClick={handleBackToLogin}
            className="text-sm text-[var(--accent-primary)] hover:text-[var(--accent-secondary)] transition-colors"
          >
            {t('userPasswordResetRequest.navigation.backToLogin', 'Back to login')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default UserPasswordResetRequestPage;
